import { ControlledTabs } from 'components/Form/ControlledTab';
import type {
    BimContractOtherStatusEnum,
    ConstructionTypeData,
    ContractOtherStatusEnum,
    ProjectMethodologyEnum,
} from 'gql/graphql';
import { OtherCostCalculationTypeEnum } from 'gql/graphql';
import type { TypeItem } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { TabsIds } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import type { IUerContractOtherCostLimitSpanSelectionData } from 'pages/Project/UerProject/Contract/hooks/useUerContractOtherCostLimitSpanSelectionList';
import { GridItem, Loader, Text } from 'uikit';

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { forwardRef, useMemo } from 'react';
import type { CSSProperties, ForwardedRef } from 'react';
import type { Control } from 'react-hook-form';
import type { UseFormClearErrors, UseFormSetValue } from 'react-hook-form/dist/types/form';

import { FormulaEditor } from '../FormulaEditor';

import { EditOtherCommon } from './EditOtherCommon';
import { EditOtherLimits } from './EditOtherLimits';

import Styles from './EditOtherForm.module.css';

export const invisibleStyle = { visibility: 'hidden', position: 'absolute', overflow: 'hidden', maxHeight: 0 };

interface IEditOtherProps {
    activeTab: { label: string; id: string };
    handleTabChange: ({ value }: any) => void;
    tabItems: { id: string; label: string }[];
    control: Control;
    fieldsToCheck: { COMMON: string[]; PRICE: string[] };
    isDataFetching: boolean;
    isGlobal?: boolean;
    globalOtherName?: string;
    chapter: any;
    selectedType: TypeItem;
    handleChangeType: (newType: any) => void;
    areFieldsDisabled: boolean;
    isChapterEditable: boolean;
    isCalculationTypeEditable: boolean;
    changeSelectedChapterOrSection: (value: any) => void;
    selectedChapterOrSection: any;
    currentConstructionTypeId: number;
    calculatingTypeValue?: number;
    selectedConstructionType: Record<string, boolean>;
    handleChangeConstructionType: (id: number, state: boolean) => void;
    constructionTypes?: ConstructionTypeData[];
    otherStatus?: BimContractOtherStatusEnum | ContractOtherStatusEnum;
    isNameFieldDisabled?: boolean;
    isFormulaFieldDisabled?: boolean;
    isModalReadOnly?: boolean;
    methodology: ProjectMethodologyEnum;
    getObjectSourceQuery?: TypedDocumentNode<any, any>;
    isOnlyTypeEditAvailable?: boolean;
    costRounding: number;
    clearErrors?: UseFormClearErrors<any>;
    setValue: UseFormSetValue<any>;
    noLimitSpan?: boolean;
    spanSelectionData?: IUerContractOtherCostLimitSpanSelectionData;
    isModify?: boolean;
    otherId: number;
    isCalculationModal?: boolean;
}

export const EditOtherForm = forwardRef(
    (
        {
            activeTab,
            handleTabChange,
            tabItems,
            control,
            fieldsToCheck,
            isDataFetching,
            isGlobal,
            globalOtherName,
            chapter,
            selectedType,
            handleChangeType,
            areFieldsDisabled,
            isChapterEditable,
            isCalculationTypeEditable,
            isNameFieldDisabled = false,
            isFormulaFieldDisabled = false,
            changeSelectedChapterOrSection,
            selectedChapterOrSection,
            currentConstructionTypeId,
            selectedConstructionType,
            handleChangeConstructionType,
            constructionTypes,
            otherStatus,
            isModalReadOnly,
            methodology,
            getObjectSourceQuery,
            isOnlyTypeEditAvailable = false,
            calculatingTypeValue,
            costRounding,
            clearErrors,
            setValue,
            noLimitSpan,
            spanSelectionData,
            isModify,
            otherId,
            isCalculationModal,
        }: IEditOtherProps,
        ref: ForwardedRef<HTMLFormElement>,
    ) => {
        const commonTabStyle = useMemo(
            () => (activeTab.id === TabsIds.COMMON ? undefined : invisibleStyle),
            [activeTab.id],
        );

        const limitsTabStyle = useMemo(
            () => (activeTab.id === TabsIds.PRICE ? undefined : invisibleStyle),
            [activeTab.id],
        );

        const formulaTabStyle = useMemo(
            () => (activeTab.id === TabsIds.FORMULA ? undefined : invisibleStyle),
            [activeTab.id],
        );

        return (
            <>
                {isDataFetching && <Loader style={{ position: 'absolute', top: '50%' }} />}

                <div className={Styles.innerBlock}>
                    <ControlledTabs
                        value={activeTab}
                        onChange={handleTabChange}
                        items={tabItems}
                        size="s"
                        control={control}
                        fieldsToCheck={fieldsToCheck}
                    />
                    {isDataFetching && <Loader style={{ position: 'absolute', top: '50%' }} />}
                    {!isDataFetching && (
                        <form ref={ref}>
                            <div className={Styles.tabsContent}>
                                <div style={commonTabStyle as CSSProperties}>
                                    <EditOtherCommon
                                        control={control}
                                        isGlobal={isGlobal}
                                        globalOtherName={globalOtherName}
                                        otherStatus={otherStatus}
                                        chapter={chapter}
                                        selectedType={selectedType}
                                        changeSelectedTypeHandler={handleChangeType}
                                        areFieldsDisabled={areFieldsDisabled}
                                        isChapterEditable={isChapterEditable}
                                        isCalculationTypeEditable={isCalculationTypeEditable}
                                        isNameFieldDisabled={isNameFieldDisabled}
                                        changeSelectedChapterOrSection={changeSelectedChapterOrSection}
                                        selectedChapterOrSection={selectedChapterOrSection}
                                        isTooltipHidden={!!commonTabStyle}
                                        isModalReadOnly={isModalReadOnly}
                                        methodology={methodology}
                                        isOnlyTypeEditAvailable={isOnlyTypeEditAvailable}
                                        calculatingTypeValue={calculatingTypeValue}
                                        isModify={isModify}
                                        otherId={otherId}
                                        isCalculationModal={isCalculationModal}
                                    />
                                </div>
                                <div style={limitsTabStyle as CSSProperties}>
                                    <EditOtherLimits
                                        control={control}
                                        currentConstructionTypeId={currentConstructionTypeId}
                                        selectedConstructionType={selectedConstructionType}
                                        changeSelectedConstructionTypeHandler={handleChangeConstructionType}
                                        areFieldsDisabled={
                                            (areFieldsDisabled || !!isModalReadOnly || isOnlyTypeEditAvailable) &&
                                            !isCalculationModal
                                        }
                                        constructionTypes={constructionTypes}
                                        isTooltipHidden={!!limitsTabStyle}
                                        costRounding={costRounding}
                                        clearErrors={clearErrors}
                                        setValue={setValue}
                                        noLimitSpan={noLimitSpan}
                                        spanSelectionData={spanSelectionData}
                                        isCalculationModal={isCalculationModal}
                                    />
                                </div>

                                <GridItem style={formulaTabStyle as CSSProperties}>
                                    {selectedType?.id === OtherCostCalculationTypeEnum?.Percent &&
                                    getObjectSourceQuery ? (
                                        <FormulaEditor
                                            name="formula"
                                            control={control}
                                            getObjectSourceQuery={getObjectSourceQuery}
                                            isDisabled={isFormulaFieldDisabled}
                                            tooltipTitle="Формула расчета"
                                            rules={{
                                                required: !isFormulaFieldDisabled && 'Обязательное поле',
                                            }}
                                            isTooltipHidden={!!formulaTabStyle}
                                            isRequired={!isFormulaFieldDisabled}
                                            isReadOnly={isModalReadOnly}
                                        />
                                    ) : (
                                        <Text view="secondary" as="span" className={Styles.formulaUnavailableText}>
                                            Для способа расчета &quot;Расчет&quot; ввод формул недоступен
                                        </Text>
                                    )}
                                </GridItem>
                            </div>
                        </form>
                    )}
                </div>
            </>
        );
    },
);

EditOtherForm.displayName = 'EditOtherForm';
